import { useRouter } from 'found';
import React, { useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { makeMutation } from '../../../../api/helpers';
import { UpdateService } from '../../../../api/mutations';
import ContentCenterer from '../../../../components/ContentCenterer';
import Button from '../../../../components/input/Button';
import Label from '../../../../components/input/Label';
import TextInput from '../../../../components/input/TextInput';
import Spacer from '../../../../components/Spacer';
import { PageTitle } from '../../../../components/typography/Typography';
import { Buttons } from '../Buttons';
import { UpdateAPI_service } from './__generated__/UpdateAPI_service.graphql';
import styled from 'styled-components';
import FlatButton from '../../../../components/input/FlatButton';
import { toBase64 } from '../../../../helpers';
import { mutationsUpdateServiceMutationVariables } from '../../../../api/__generated__/mutationsUpdateServiceMutation.graphql';
import { useToastDispatch } from '../../../../components/Toast/ToastProvider';
import { routes } from '../../../../routes';

const LogoSection = styled.div``;

const Container = styled.div`
  display: flex;
  justify-content: center;
  flex: 1;
`;

const LogoPreview = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 18%;
  background-size: cover;
  box-shadow: 0px 3px 6px #0000001f;
`;

const LogoContent = styled.label`
  display: flex;
  align-items: center;
`;

const FileInput = styled.input`
  display: none;
`;

type State = {
  name: string;
  description: string;
  imageURL: string;
  image: mutationsUpdateServiceMutationVariables['image'];
};

type Props = {
  service: UpdateAPI_service;
};

function UpdateAPI({ service }: Props) {
  const [formState, setFormState] = useState<State>({
    name: service.name,
    description: service.description,
    imageURL: service.imageURL,
    image: undefined,
  });

  const { router } = useRouter();
  const dispatch = useToastDispatch();

  // onClose go back to previous page
  const onClose = () => {
    router.go(-1);
  };

  const onSave = async () => {
    const variables: mutationsUpdateServiceMutationVariables = {
      serviceID: service.id,
      name: formState.name,
      description: formState.description,
      image: formState.image,
    };

    try {
      await makeMutation(UpdateService, variables);
      dispatch({
        text: `${service.name} updated successfully`,
        alert: 'success',
      });
      router.push(`${routes.api}/${service.id}`);
    } catch (err) {
      dispatch({ text: err.message, alert: 'error', stayFor: 4000 });
    }
  };

  const onUpload = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const files = evt?.target?.files;
    if (files && files.length > 0) {
      const file = files[0];
      const split = file.name.split('.');
      const fType = split[split.length - 1].toLowerCase();
      if (fType !== 'jpg' && fType !== 'jpeg' && fType !== 'png') {
        dispatch({
          text: 'Pick a png or jpg image',
          alert: 'error',
          stayFor: 6000,
        });
        return;
      }
      try {
        const base64 = await toBase64(file);
        if (!base64) {
          dispatch({
            text: 'Unable to upload image, please try again',
            alert: 'error',
            stayFor: 6000,
          });
          throw new Error('Base64 is undefined');
        }
        setFormState((val) => ({
          ...val,
          imageURL: URL.createObjectURL(file),
          image: {
            imageBase64: base64,
            fileType: fType,
          },
        }));
      } catch (err) {
        dispatch({
          text: 'Unable to upload image, please try again',
          alert: 'error',
          stayFor: 6000,
        });
        console.error('Unable to base64 image', err);
      }
    }
  };

  return (
    <Container>
      <ContentCenterer maxWidth={'838px'}>
        <Spacer spacing={4} vertical />
        <PageTitle>Update API details</PageTitle>
        <Spacer vertical spacing={4} />
        <TextInput
          label={'API NAME'}
          value={formState.name}
          onChange={(text) => setFormState((val) => ({ ...val, name: text }))}
        />
        <Spacer vertical spacing={2} />
        <TextInput
          label={'API DESCRIPTION'}
          value={formState.description}
          onChange={(text) =>
            setFormState((val) => ({ ...val, description: text }))
          }
        />
        <Spacer vertical spacing={2} />
        <LogoSection>
          <Label>API LOGO</Label>
          <Spacer vertical spacing={1} />
          <FileInput
            type='file'
            name='file'
            id={'logo-upload'}
            onChange={onUpload}
          />
          <LogoContent htmlFor={'logo-upload'}>
            <LogoPreview
              style={{ backgroundImage: `url(${formState.imageURL})` }}
            />
            <Spacer horizontal spacing={2} />
            <FlatButton darkenOnHover color='#5C6EF8'>
              Upload image
            </FlatButton>
          </LogoContent>
        </LogoSection>
        <Spacer vertical spacing={4} />
        <Buttons>
          <Button background={'#bab8c5'} color={'white'} onClick={onClose}>
            Cancel
          </Button>
          <Button color={'white'} onClick={onSave}>
            Save
          </Button>
        </Buttons>
      </ContentCenterer>
    </Container>
  );
}

export default createFragmentContainer(UpdateAPI, {
  service: graphql`
    fragment UpdateAPI_service on Service {
      id
      name
      description
      imageURL
    }
  `,
});
